import { HttpErrorResponse } from '@angular/common/http';
import { Renderer2 } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { GlobalComponentService } from '@techmore/global';
import { throwError } from 'rxjs';
import { OAuthError } from './share/entity/OauthError';
import { Router } from '@angular/router';
import { Response } from 'express';
import { LoggerService } from './share/service/logger.service';


export const checkIsMobile = () => {
  return /mobile|Android|iPad|iPhone/.test(window.navigator.userAgent);
};

export function loadLazyCssFile(renderer: Renderer2, parentEl: Element, styleName: string, path: string, preload?: boolean) {
  if (typeof document !== 'undefined' && document.getElementById(styleName)) { return; }
  const style = renderer.createElement('link');
  const noscriptStyle = renderer.createElement('link');
  renderer.setAttribute(style, 'id', `${styleName}`);
  renderer.setAttribute(noscriptStyle, 'id', `noscript_${styleName}`);
  renderer.setAttribute(style, 'href', `${path}`);
  renderer.setAttribute(noscriptStyle, 'href', `${path}`);
  renderer.setAttribute(style, 'onload', `this.rel='stylesheet'`);
  if (preload) {
    renderer.setAttribute(style, 'rel', `preload`);
    renderer.setAttribute(noscriptStyle, 'rel', `stylesheet`);
    renderer.setAttribute(style, 'as', `style`);
  } else {
    renderer.setAttribute(style, 'rel', `stylesheet`);
  }
  const body = parentEl.closest('body');
  renderer.insertBefore(body, style, body.firstElementChild);

  if (preload) {
    const bodyNoScript = body.querySelectorAll('noscript')[2];
    if (bodyNoScript.childElementCount === 0) {
      renderer.appendChild(bodyNoScript, noscriptStyle);
    } else {
      renderer.insertBefore(bodyNoScript, noscriptStyle, bodyNoScript.firstElementChild);
    }
  }
}

export function loadLazyJsFile(renderer: Renderer2, scriptName: string, path: string, useDefer?: boolean) {
  if (document.getElementById(scriptName)) { return; }
  const script = renderer.createElement('script');
  renderer.setAttribute(script, 'id', `${scriptName}`);
  renderer.setAttribute(script, 'type', `text/javascript`);
  renderer.setAttribute(script, 'src', `${path}`);
  renderer.setAttribute(script, 'async', ``);
  if (useDefer) {
    renderer.setAttribute(script, 'defer', ``);
  }
  // 用 document 塞在 server 端部會有作用, 但沒關係這個在 client 端在執行就好
  // 用 parentEl.closest('body').previousSiblingsElement appendChild 也沒用 (但 insertBefore 沒試過)
  renderer.appendChild(document.head, script);
}

export function closestPolifill() {
  if (!Element.prototype.matches) {
    Element.prototype.matches =
      (Element.prototype as any).msMatchesSelector ||
      Element.prototype.webkitMatchesSelector;
  }

  if (!Element.prototype.closest) {
    Element.prototype.closest = function (s) {
      let el = this;

      do {
        if (typeof s === 'string' && Element.prototype.matches.call(el, s)) {
          return el;
        } else if (typeof s === 'object' && el === s) { return el; }
        el = el.parentElement || el.parentNode;
      } while (el !== null && el.nodeType === 1);
      return null;
    };
  }
}

/** 可餵入 HTMLElement做目標比對的 closest 方法 */
export const closest = (el, s) => {
  do {
    if (typeof s === 'string') {
      if (Element.prototype.matches.call(el, s)) { return el; }
    } else if (s instanceof HTMLElement) {
      if (el === s) { return el; }
    }
    el = el.parentElement || el.parentNode;
  } while (el !== null && el.nodeType === 1);
  return null;
};


/** 將表格的查詢參數轉換成查 API 用的查詢參數 */
export function transferTableParams2HttpListParams(queryParam: any) {
  let params: { [param: string]: string | string[]; } = {};
  if (queryParam.limit) {
    params['size'] = `${queryParam.limit}`;
  }
  if (queryParam.filter) {
    params['filter'] = `${queryParam.filter}`;
  }
  if (queryParam.search) {
    params['search'] = `${queryParam.search}`;
  }
  if (queryParam.limit && (queryParam.offset || queryParam.offset === 0)) {
    params['page'] = `${queryParam.offset / queryParam.limit}`;
  }
  if (queryParam.sort && queryParam.order) {
    params['sort'] = `${queryParam.sort},${queryParam.order}`;
  }
  if (queryParam.query) {
    const emptyQueries = ['null', '', 'undefined'];
    const fallbackQuery = emptyQueries.includes(queryParam.query) ? '{}' : queryParam.query;
    const query = JSON.parse(fallbackQuery);
    const keys = Object.keys(query);
    if (keys.length !== 0) {
      for (const k of keys) {
        if (query[k] !== null && typeof query[k] !== 'undefined') {
          params[k] = query[k];
        }
      }
    }
  }

  return params;
}

export function transformTableParamsToOffsetLimitListParams(queryParam: any) {
  let params: { [param: string]: string | string[]; } = {};
  if (queryParam.limit) {
    params['limit'] = `${queryParam.limit}`;
  }
  if (queryParam.filter) {
    params['filter'] = `${queryParam.filter}`;
  }
  if (queryParam.search) {
    params['search'] = `${queryParam.search}`;
  }
  if (queryParam.limit && (queryParam.offset || queryParam.offset === 0)) {
    params['offset'] = `${queryParam.offset}`;
  }
  if (queryParam.sort && queryParam.order) {
    params['sort'] = `${queryParam.sort},${queryParam.order}`;
  }
  if (queryParam.query) {
    const emptyQueries = ['null', '', 'undefined'];
    const fallbackQuery = emptyQueries.includes(queryParam.query) ? '{}' : queryParam.query;
    const query = JSON.parse(fallbackQuery);
    const keys = Object.keys(query);
    if (keys.length !== 0) {
      for (const k of keys) {
        if (query[k] !== null && typeof query[k] !== 'undefined') {
          params[k] = query[k];
        }
      }
    }
  }

  return params;
}

















/** 將物件中為定義的值轉成 null */
export function transferObjNullVal(customSearchEntity: any) {
  for (let keys = Object.keys(customSearchEntity), i = 0; i < keys.length; i++) {
    const key = keys[i];
    if (typeof customSearchEntity[key] === 'undefined') {
      customSearchEntity[key] = null;
    }
  }
}

/** 登入後操作 api 若失敗的訊息處理方式 */
export function printErrorHandler(error: any, reqUrl: string, translateService: TranslateService, globalComponentService: GlobalComponentService, words: { [key: string]: string }, isBrowser: boolean, loggerService: LoggerService) {
  // 如果是下載或單筆或多筆模組的 API 回傳 500 錯誤，且subStatus為999，不再顯示錯誤訊息，直接throwError
  const isSuppressErrorDisplay = /cm\/(download|single|normal)/.test(reqUrl) && error.status === 500 && error.error.subStatus === '999';
  if (isSuppressErrorDisplay) {
    return throwError(error);
  }
  if (error instanceof HttpErrorResponse) {
    switch (error.status) {
      case 404:
        loggerService.error(`${words['addressNotFound']} : ${reqUrl}`);
        break;
      case 401:
        // 401 是權控丟的，交給 BackStageTemplateService、SsoAuthService 處理
        // 除了機器人驗證失敗的訊息
        try {
          loggerService.error(error.error.message);
        } catch (e) {
          loggerService.error(`${words['undefinedError']}，URL: ${reqUrl}`, e);
        }
        break;
      default:
        try {
          let oauthError: OAuthError = Object.assign(new OAuthError(translateService), error.error);
          if (oauthError instanceof Blob) {
            const reader = new FileReader();
            reader.onload = () => {
              oauthError = JSON.parse(reader.result.toString());
              loggerService.error(`${oauthError.getMessage()}，URL: ${reqUrl}`);
            };
            reader.readAsText(oauthError);
          } else {
            loggerService.error(`${oauthError.getMessage()}，URL: ${reqUrl}`);
          }
        } catch (e) {
          loggerService.error(`${words['undefinedError']}，URL: ${reqUrl}`, e);
        }
        break;
    }
  }
  return throwError(error);
}

/** 當檔案上傳失敗事件拋出 */
export function onUploadFailedEvent(evt, globalComponentService: GlobalComponentService, isBrowser: boolean) {
  console.error(evt);

  if (isBrowser) {
    switch (evt.code) {
      case 'E1':
        globalComponentService.openErrorAlert((evt.fileName || '') + '檔案格式錯誤');
        break;
      case 'E2':
        globalComponentService.openErrorAlert((evt.fileName || '') + '檔案大小錯誤');
        break;
      case 'E3':
        globalComponentService.openErrorAlert((evt.fileName || '') + '匯入失敗: ' + (evt.msg as Error).message);
        break;
    }
  }
}

/** 打亂陣列排序 */
export function randomData(data: any[]): any[] {
  // tslint:disable-next-line: only-arrow-functions
  data.sort(function () {
    return (0.5 - Math.random());
  });
  return data;
}

export function removeUrlQueryParam(url: string): string {
  const queryIndex = url.indexOf('?');
  if (queryIndex > 0) {
    return url.substring(0, queryIndex);
  }
  return url;
}

export function parseQueryParams(url: string): Record<string, any> {
  const queryParamStartIndex = url.indexOf('?');
  if (queryParamStartIndex === -1) {
    return {};
  }
  const urlQueryParam = url.slice(queryParamStartIndex + 1, url.length);
  const queryParam = {};
  urlQueryParam.split('&').forEach(qps => {
    const a = qps.split('=');
    queryParam[a[0]] = a[1];
  });
  return queryParam;
}

export function isIOS() {
  return [
    'iPad Simulator',
    'iPhone Simulator',
    'iPod Simulator',
    'iPad',
    'iPhone',
    'iPod'
  ].includes(navigator.platform)
    // iPad on iOS 13 detection
    || (navigator.userAgent.includes('Mac') && 'ontouchend' in document);
}


/** 解析 JWT token */
export function parseJWTtoken(accessToken: string) {
  const base64Url = accessToken.split('.')[1];
  let base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  switch (base64.length % 4) {
    case 0: break;
    case 2: base64 += '=='; break;
    case 3: base64 += '='; break;
    default: break;
  }

  const jwtPayloadStr = decodeURIComponent(escape(atob(base64)));
  return JSON.parse(jwtPayloadStr);
}

/** 檢查是否為觸控螢幕裝置 */
export function checkIsTouchMobile(): boolean {
  let hasTouchScreen = false;
  const navigator: Navigator = window.navigator;
  if ('maxTouchPoints' in navigator) {
    hasTouchScreen = navigator.maxTouchPoints > 0;
  } else if ('msMaxTouchPoints' in navigator) {
    hasTouchScreen = (navigator as Navigator)['msMaxTouchPoints'] > 0;
  } else {
    const mQ = window.matchMedia && matchMedia('(pointer:coarse)');
    if (mQ && mQ.media === '(pointer:coarse)') {
      hasTouchScreen = !!mQ.matches;
    } else if ('orientation' in window) {
      hasTouchScreen = true; // deprecated, but good fallback
    } else {
      // Only as a last resort, fall back to user agent sniffing
      const UA = (navigator as Navigator).userAgent;
      hasTouchScreen = (
        /\b(BlackBerry|webOS|iPhone|IEMobile)\b/i.test(UA) ||
        /\b(Android|Windows Phone|iPad|iPod)\b/i.test(UA)
      );
    }
  }
  return hasTouchScreen;
}

/** 在 server side 和 client side 做路徑轉導 */
export function navigateCrossPlatform(isBrowser: boolean, router: Router, response: Response, path: string[]): Promise<any> | Response {
  if (isBrowser) {
    return router.navigate(path);
  }
  response.redirect(301, path.join('/'));
  response.end();
  return response;
}
